import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Progress } from '@/components/ui/progress'
import { Church, Loader2, CheckCircle, ArrowRight, ArrowLeft, Building2, User, CreditCard, Users } from 'lucide-react'
import { supabase } from '@/lib/supabase'

interface ChurchRegistrationFormProps {
  open: boolean
  onClose: () => void
}

type FormStep = 1 | 2 | 3 | 'success'

export default function ChurchRegistrationForm({ open, onClose }: ChurchRegistrationFormProps) {
  const [currentStep, setCurrentStep] = useState<FormStep>(1)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    // Church Info
    churchName: '',
    churchAddress: '',
    churchCity: '',
    churchCountry: 'Ghana',
    churchPhone: '',
    churchEmail: '',
    denomination: '',
    estimatedMembers: '',

    // Pastor Info
    pastorTitle: 'Pastor',
    pastorFirstName: '',
    pastorLastName: '',
    pastorEmail: '',
    pastorPhone: '',

    // Additional Info
    desiredPlan: 'starter',
    hearAboutUs: '',
    additionalInfo: '',
  })

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const validateStep = (step: number): boolean => {
    if (step === 1) {
      return !!(
        formData.churchName &&
        formData.churchAddress &&
        formData.churchCity &&
        formData.churchCountry &&
        formData.churchPhone &&
        formData.churchEmail &&
        formData.estimatedMembers
      )
    }
    if (step === 2) {
      return !!(
        formData.pastorFirstName &&
        formData.pastorLastName &&
        formData.pastorEmail &&
        formData.pastorPhone
      )
    }
    return true
  }

  const handleNext = () => {
    if (currentStep === 1 && validateStep(1)) {
      setCurrentStep(2)
    } else if (currentStep === 2 && validateStep(2)) {
      setCurrentStep(3)
    }
  }

  const handleBack = () => {
    if (currentStep === 2) setCurrentStep(1)
    else if (currentStep === 3) setCurrentStep(2)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    try {
      // Create church invitation/application with correct field names
      const { error } = await supabase
        .from('church_invitations')
        .insert({
          church_name: formData.churchName,
          church_email: formData.churchEmail,
          church_phone: formData.churchPhone,
          owner_email: formData.pastorEmail,
          owner_first_name: formData.pastorFirstName,
          owner_last_name: formData.pastorLastName,
          owner_phone: formData.pastorPhone,
          status: 'pending',
          invitation_details: {
            church_address: formData.churchAddress,
            church_city: formData.churchCity,
            church_country: formData.churchCountry,
            denomination: formData.denomination,
            estimated_members: formData.estimatedMembers,
            pastor_title: formData.pastorTitle,
            desired_plan: formData.desiredPlan,
            hear_about_us: formData.hearAboutUs,
            additional_info: formData.additionalInfo,
            submission_date: new Date().toISOString(),
            source: 'website_registration'
          }
        })

      if (error) throw error

      setCurrentStep('success')
    } catch (error) {
      console.error('Error submitting registration:', error)
      alert('Failed to submit registration. Please try again or contact support.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (currentStep === 'success') {
      setCurrentStep(1)
      setFormData({
        churchName: '',
        churchAddress: '',
        churchCity: '',
        churchCountry: 'Ghana',
        churchPhone: '',
        churchEmail: '',
        denomination: '',
        estimatedMembers: '',
        pastorTitle: 'Pastor',
        pastorFirstName: '',
        pastorLastName: '',
        pastorEmail: '',
        pastorPhone: '',
        desiredPlan: 'starter',
        hearAboutUs: '',
        additionalInfo: '',
      })
    }
    onClose()
  }

  const getProgressValue = () => {
    if (currentStep === 1) return 33
    if (currentStep === 2) return 66
    if (currentStep === 3) return 100
    return 100
  }

  // Success Screen
  if (currentStep === 'success') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg bg-white dark:bg-slate-900">
          <div className="text-center py-8">
            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="h-12 w-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-3">Application Submitted!</h2>
            <p className="text-slate-600 dark:text-slate-400 text-lg mb-8">
              Thank you for choosing FaithYoke. Your application is being reviewed.
            </p>

            <div className="bg-sky-50 dark:bg-sky-900/20 border-2 border-sky-200 dark:border-sky-800 rounded-xl p-6 mb-8 text-left">
              <h3 className="font-semibold text-sky-900 dark:text-sky-100 mb-3 flex items-center gap-2">
                <Church className="h-5 w-5" />
                What Happens Next?
              </h3>
              <ul className="space-y-2 text-sm text-sky-800 dark:text-sky-200">
                <li className="flex items-start gap-2">
                  <span className="font-bold mt-0.5">1.</span>
                  <span>Our team will review your application within <strong>1-2 business days</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold mt-0.5">2.</span>
                  <span>You'll receive an email at <strong>{formData.pastorEmail}</strong> with your login credentials</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold mt-0.5">3.</span>
                  <span>Access your church dashboard and start managing your ministry</span>
                </li>
              </ul>
            </div>

            <Button
              onClick={handleClose}
              className="w-full bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 h-12 text-lg"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  // Main Form
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900">
        <DialogHeader>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 text-white">
                  <Church className="h-7 w-7" />
                </div>
                <div>
                  <DialogTitle className="text-2xl">Register Your Church</DialogTitle>
                  <DialogDescription className="text-base">
                    Step {currentStep} of 3
                  </DialogDescription>
                </div>
              </div>
            </div>
            <Progress value={getProgressValue()} className="h-2" />
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Church Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 pb-4 border-b">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 dark:bg-sky-900/30">
                  <Building2 className="h-6 w-6 text-sky-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Church Information</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Tell us about your church</p>
                </div>
              </div>

              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="churchName">Church Name *</Label>
                  <Input
                    id="churchName"
                    value={formData.churchName}
                    onChange={(e) => updateField('churchName', e.target.value)}
                    placeholder="Grace Baptist Church"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="churchAddress">Address *</Label>
                  <Input
                    id="churchAddress"
                    value={formData.churchAddress}
                    onChange={(e) => updateField('churchAddress', e.target.value)}
                    placeholder="123 Main Street"
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="churchCity">City *</Label>
                    <Input
                      id="churchCity"
                      value={formData.churchCity}
                      onChange={(e) => updateField('churchCity', e.target.value)}
                      placeholder="Accra"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="churchCountry">Country *</Label>
                    <Select value={formData.churchCountry} onValueChange={(value) => updateField('churchCountry', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Ghana">🇬🇭 Ghana</SelectItem>
                        <SelectItem value="Nigeria">🇳🇬 Nigeria</SelectItem>
                        <SelectItem value="Kenya">🇰🇪 Kenya</SelectItem>
                        <SelectItem value="Uganda">🇺🇬 Uganda</SelectItem>
                        <SelectItem value="South Africa">🇿🇦 South Africa</SelectItem>
                        <SelectItem value="Other">🌍 Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="churchPhone">Phone *</Label>
                    <Input
                      id="churchPhone"
                      type="tel"
                      value={formData.churchPhone}
                      onChange={(e) => updateField('churchPhone', e.target.value)}
                      placeholder="+233 XX XXX XXXX"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="churchEmail">Email *</Label>
                    <Input
                      id="churchEmail"
                      type="email"
                      value={formData.churchEmail}
                      onChange={(e) => updateField('churchEmail', e.target.value)}
                      placeholder="info@church.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="denomination">Denomination (Optional)</Label>
                    <Input
                      id="denomination"
                      value={formData.denomination}
                      onChange={(e) => updateField('denomination', e.target.value)}
                      placeholder="Baptist, Methodist, etc."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="estimatedMembers">Number of Members *</Label>
                    <Select value={formData.estimatedMembers} onValueChange={(value) => updateField('estimatedMembers', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-50">1 - 50 members</SelectItem>
                        <SelectItem value="51-100">51 - 100 members</SelectItem>
                        <SelectItem value="101-250">101 - 250 members</SelectItem>
                        <SelectItem value="251-500">251 - 500 members</SelectItem>
                        <SelectItem value="501-1000">501 - 1,000 members</SelectItem>
                        <SelectItem value="1001-2000">1,001 - 2,000 members</SelectItem>
                        <SelectItem value="2001+">2,001+ members</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleNext}
                  disabled={!validateStep(1)}
                  className="flex-1 bg-gradient-to-r from-sky-500 to-cyan-500"
                >
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Pastor Information */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 pb-4 border-b">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 dark:bg-sky-900/30">
                  <User className="h-6 w-6 text-sky-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Pastor Information</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Primary contact for your church</p>
                </div>
              </div>

              <div className="grid gap-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pastorTitle">Title *</Label>
                    <Select value={formData.pastorTitle} onValueChange={(value) => updateField('pastorTitle', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pastor">Pastor</SelectItem>
                        <SelectItem value="Reverend">Reverend</SelectItem>
                        <SelectItem value="Bishop">Bishop</SelectItem>
                        <SelectItem value="Elder">Elder</SelectItem>
                        <SelectItem value="Minister">Minister</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pastorFirstName">First Name *</Label>
                    <Input
                      id="pastorFirstName"
                      value={formData.pastorFirstName}
                      onChange={(e) => updateField('pastorFirstName', e.target.value)}
                      placeholder="John"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pastorLastName">Last Name *</Label>
                    <Input
                      id="pastorLastName"
                      value={formData.pastorLastName}
                      onChange={(e) => updateField('pastorLastName', e.target.value)}
                      placeholder="Mensah"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pastorEmail">Email *</Label>
                    <Input
                      id="pastorEmail"
                      type="email"
                      value={formData.pastorEmail}
                      onChange={(e) => updateField('pastorEmail', e.target.value)}
                      placeholder="pastor@church.com"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pastorPhone">Phone *</Label>
                    <Input
                      id="pastorPhone"
                      type="tel"
                      value={formData.pastorPhone}
                      onChange={(e) => updateField('pastorPhone', e.target.value)}
                      placeholder="+233 XX XXX XXXX"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={handleBack} className="flex-1">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={handleNext}
                  disabled={!validateStep(2)}
                  className="flex-1 bg-gradient-to-r from-sky-500 to-cyan-500"
                >
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Plan Selection */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 pb-4 border-b">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 dark:bg-sky-900/30">
                  <CreditCard className="h-6 w-6 text-sky-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Choose Your Plan</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Select the best plan for your church</p>
                </div>
              </div>

              <div className="grid gap-3">
                {[
                  { value: 'starter', name: 'Starter', price: 'GHS 199/mo', desc: 'Up to 500 members', features: ['Member management', 'Mobile app', '300 SMS/month'] },
                  { value: 'growth', name: 'Growth', price: 'GHS 499/mo', desc: 'Up to 2,000 members', features: ['Financial management', 'Attendance tracking', '1,000 SMS/month'], popular: true },
                  { value: 'leadership', name: 'Leadership', price: 'GHS 1,499/mo', desc: 'Unlimited members', features: ['HR & Payroll', 'Multi-location', '3,000 SMS/month'] }
                ].map((plan) => (
                  <label
                    key={plan.value}
                    className={`cursor-pointer border-2 rounded-xl p-4 transition-all ${
                      formData.desiredPlan === plan.value
                        ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20 shadow-md'
                        : 'border-slate-200 dark:border-slate-700 hover:border-sky-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="desiredPlan"
                      value={plan.value}
                      checked={formData.desiredPlan === plan.value}
                      onChange={(e) => updateField('desiredPlan', e.target.value)}
                      className="sr-only"
                    />
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="font-semibold text-lg">{plan.name}</div>
                          {plan.popular && (
                            <span className="text-xs bg-gradient-to-r from-sky-500 to-cyan-500 text-white px-2 py-0.5 rounded-full">
                              Popular
                            </span>
                          )}
                        </div>
                        <div className="text-slate-600 dark:text-slate-400 mb-2">{plan.price}</div>
                        <div className="text-sm text-slate-500 mb-2">{plan.desc}</div>
                        <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                          {plan.features.map((feature, idx) => (
                            <li key={idx}>• {feature}</li>
                          ))}
                        </ul>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.desiredPlan === plan.value
                          ? 'border-sky-500 bg-sky-500'
                          : 'border-slate-300'
                      }`}>
                        {formData.desiredPlan === plan.value && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="space-y-4 pt-2">
                <div className="space-y-2">
                  <Label htmlFor="hearAboutUs">How did you hear about us?</Label>
                  <Select value={formData.hearAboutUs} onValueChange={(value) => updateField('hearAboutUs', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="google">Google Search</SelectItem>
                      <SelectItem value="social">Social Media</SelectItem>
                      <SelectItem value="referral">Referral from another church</SelectItem>
                      <SelectItem value="event">Conference/Event</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="additionalInfo">Additional Information (Optional)</Label>
                  <Textarea
                    id="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={(e) => updateField('additionalInfo', e.target.value)}
                    placeholder="Any additional details..."
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={handleBack} className="flex-1">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-gradient-to-r from-sky-500 to-cyan-500"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Application
                      <CheckCircle className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  )
}
